import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { TrendingUp, DollarSign, ShoppingBag, Check, Ticket } from 'lucide-react';
import { adminFetch, statusColors } from './shared';

export const StatsTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await adminFetch(`/api/admin/stats`);
        if (res.ok) setStats(await res.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="text-center py-12 text-slate-500">Cargando estadísticas...</div>;
  if (!stats) return <div className="text-center py-12 text-slate-500">Error al cargar estadísticas</div>;

  const methodLabels = { mercadopago: 'Mercado Pago', paypal: 'PayPal', transferencia: 'Transferencia', desconocido: 'Otro' };

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total_orders}</p>
              <p className="text-xs text-slate-500">Total Pedidos</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">${stats.total_revenue?.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Ingresos Totales</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">${stats.avg_order?.toFixed(0)}</p>
              <p className="text-xs text-slate-500">Ticket Promedio</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Check className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.completed_orders}</p>
              <p className="text-xs text-slate-500">Completados</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Orders by Status */}
      <Card className="p-5 bg-white">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Pedidos por Estado</h3>
        <div className="space-y-3">
          {Object.entries(stats.orders_by_status || {}).map(([status, count]) => {
            const maxCount = Math.max(...Object.values(stats.orders_by_status));
            const width = maxCount > 0 ? (count / maxCount) * 100 : 0;
            return (
              <div key={status} className="flex items-center gap-3">
                <span className="text-xs text-slate-600 w-24 shrink-0">{status}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${statusColors[status] || 'bg-slate-400'} flex items-center justify-end px-2 transition-all`}
                    style={{ width: `${Math.max(width, 8)}%` }}
                  >
                    <span className="text-xs text-white font-medium">{count}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Revenue by Payment Method */}
      <Card className="p-5 bg-white">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Ingresos por Método de Pago</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(stats.orders_by_method || []).map(item => (
            <div key={item.method} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-slate-700">{methodLabels[item.method] || item.method}</p>
                <p className="text-xs text-slate-500">{item.count} pedidos</p>
              </div>
              <p className="font-bold text-cyan-600">${item.revenue?.toFixed(2)}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Top Products */}
      {stats.top_products?.length > 0 && (
        <Card className="p-5 bg-white">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Productos Más Vendidos</h3>
          <div className="space-y-2">
            {stats.top_products.map((product, idx) => (
              <div key={`top-${product.name}`} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-slate-400 w-6">#{idx + 1}</span>
                  <span className="text-sm text-slate-700">{product.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-800">{product.qty} uds.</p>
                  <p className="text-xs text-slate-500">${product.revenue?.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Daily Orders Chart */}
      {stats.daily_orders?.length > 0 && (
        <Card className="p-5 bg-white">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Pedidos Últimos 7 Días</h3>
          <div className="flex items-end gap-2 h-32">
            {stats.daily_orders.map(day => {
              const maxCount = Math.max(...stats.daily_orders.map(d => d.count));
              const height = maxCount > 0 ? (day.count / maxCount) * 100 : 10;
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-medium text-slate-700">{day.count}</span>
                  <div 
                    className="w-full bg-cyan-500 rounded-t-md transition-all"
                    style={{ height: `${Math.max(height, 5)}%` }}
                  />
                  <span className="text-[10px] text-slate-500">{day.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};


// ==================== ACTIVITY TAB ====================
